import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';

export const saleIndexStore = defineStore('saleIndexStore', () => {
    const sales = ref([]);
    const loaded = ref(false);
    const loading = ref(false);

    // itemId -> the lane it runs in. A sale document is one LANE, and its
    // runList carries real catalog item `_id`s — the same ids the feed's
    // documents use — so this is a plain string keying, no translation.
    const byItemId = computed(() => {
        const map = new Map();
        for (const sale of sales.value) {
            const saleId = idOf(sale);
            if (!saleId) continue;
            const runList = Array.isArray(sale.runList) ? sale.runList : [];
            runList.forEach((itemId, index) => {
                // First lane wins. An item shouldn't be in two lanes at once —
                // the runtime rejects a duplicate runList id across events — so
                // a collision means the seed is wrong, not that we should guess.
                if (map.has(itemId)) return;
                map.set(itemId, {
                    saleId,
                    saleName: sale.saleName,
                    laneName: sale.laneName ?? null,
                    eventType: sale.eventType,
                    location: sale.location ?? null,
                    auctionHouseCode: sale.auctionHouseCode ?? null,
                    startAt: sale.startAt ?? null,
                    runNumber: index
                });
            });
        }
        return map;
    });

    // Load once per session. Never throws: a failed load leaves the map empty and
    // every card renders unscheduled, which is exactly the intended degradation.
    const ensureLoaded = async () => {
        if (loaded.value || loading.value) return;
        loading.value = true;
        try {
            sales.value = await autoAuctionApiSdk.getSales();
            loaded.value = true;
        } catch (err) {
            console.warn('Sale index load failed — cards will render as unscheduled', err);
        } finally {
            loading.value = false;
        }
    };

    return {
        sales,
        loaded,
        loading,
        byItemId,
        ensureLoaded,
        saleFor: (itemId) =>
            (itemId ? byItemId.value.get(String(itemId)) ?? null : null)
    };
});

export default saleIndexStore;
